// const MapboxDraw = require('@mapbox/mapbox-gl-draw');
// import MapboxDraw from "./mapbox-gl-draw";
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import doubleClickZoom from './utils/double_click_zoom';
import * as Constants from './utils/constants';

import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './utils/CalculateSnapTolerance';
import {cursors} from './utils/constants';

import * as turf from '@turf/turf';
import { drawModes, drawState, Source } from '../../Types';

let CircleMode:drawModes = <drawModes><unknown>{...MapboxDraw.modes.draw_polygon};

CircleMode.onSetup = function(opts:any) {
  const polygon = this.newFeature({
    type: Constants.geojsonTypes.FEATURE,
    properties: {
      isCircle: true,
      center: []
    },
    geometry: {
      type: Constants.geojsonTypes.POLYGON,
      coordinates: [[]]
    }
  });

  this.addFeature(polygon);

  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);
  this.updateUIClasses({ mouse: Constants.cursors.ADD });
  this.activateUIButton(Constants.types.POLYGON);
  this.setActionableState({
    trash: true
  });

  const state:drawState = <drawState>{
    currentVertexPosition: 0,
    polygon,
    keepVisibleFeatureIds: Array.isArray(opts?.keepVisibleFeatureIds)
      ? opts.keepVisibleFeatureIds.map(String)
      : [],
  };

  state.options = sanitizeSnapOptions(this._ctx.options);

  const optionsChangedCallBAck = (options: drawState['options']) => {
    state.options = sanitizeSnapOptions(options);
    if (!state.options.snap) {
      clearSnapMarkerOnMap(this.map);
    }
  };

  state.optionsChangedCallBAck = optionsChangedCallBAck;
  this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

  this.state = state;

  this.snapMarkerSource = (<Source>this.map.getSource('geobox-gl-snap-marker'))._data;

  return state;
};

CircleMode.onTap = function (state:drawState, e:any) {
  this.onMouseMove(state, e, true);
  this.onClick(state, e);
};

CircleMode.clickAnywhere = function(state:drawState, e:any) {
  // Refresh snap at click time so the center/edge use the locked vertex.
  if (e) {
    CircleMode.onMouseMove.call(this, state, e, false);
  }

  const lng = this.state.snappedLng;
  const lat = this.state.snappedLat;

  state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, lng, lat);

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, lng, lat, {
      locked: Boolean((this.state as any)._snapLocked),
      kind: (this.state as any)._snapKind ?? null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }

  state.currentVertexPosition++;

  if (state.currentVertexPosition === 2) {
    const from = turf.point(state.polygon.coordinates[0][0]);
    const to = turf.point((<any>state.polygon.coordinates[0])[1]);

    const distance = turf.distance(from, to);

    state.currentVertexPosition++;
    const center = state.polygon.coordinates[0][0];

    const circleFeature = turf.circle(center, distance, { steps: 100 });
    state.polygon.incomingCoords(circleFeature.geometry.coordinates);
    state.polygon.properties.center = center;
    state.polygon.properties.radiusInKm = distance;

    clearSnapMarkerOnMap(this.map);
    return this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
  }
};

CircleMode.onMouseMove = function (state:drawState, e:any, touch:boolean) {
  touch = touch || false;
  this.map.getCanvasContainer().style.cursor = "crosshair";
  const { lng, lat } = e.lngLat;

  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;

  if (this.state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, this.state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (this.state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    this.state.snappedLng = desiredCoord[0];
    this.state.snappedLat = desiredCoord[1];
  } else {
    this.state.snappedLng = lng;
    this.state.snappedLat = lat;
  }

  (this.state as any)._snapLocked = locked;
  (this.state as any)._snapKind = locked ? snapKind ?? null : null;

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, this.state.snappedLng, this.state.snappedLat, {
      locked,
      kind: locked ? snapKind ?? null : null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }

  state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, this.state.snappedLng, this.state.snappedLat);

  if (
    this.state.lastVertex
    && this.state.lastVertex[0] === lng
    && this.state.lastVertex[1] === lat
  ) {
    this.updateUIClasses({ mouse: cursors.POINTER });
  } else {
    this.updateUIClasses({ mouse: cursors.ADD });
  }
};

/** Only override display for create-hole parent visibility; circle sketch uses stock draw_polygon preview. */
CircleMode.toDisplayFeatures = function (state: drawState, geojson: any, display: any) {
  const keepVisible = (state.keepVisibleFeatureIds ?? []).map(String);
  if (keepVisible.length > 0) {
    const sketchId = String(state.polygon?.id ?? '');
    const featureId = String(geojson.properties?.id ?? '');
    if (sketchId && featureId && featureId !== sketchId) {
      geojson.properties.active = keepVisible.includes(featureId)
        ? Constants.activeStates.ACTIVE
        : Constants.activeStates.INACTIVE;
      return display(geojson);
    }
  }

  return (MapboxDraw.modes.draw_polygon.toDisplayFeatures as Function).call(
    this,
    state,
    geojson,
    display
  );
};

CircleMode.onStop = function (state: drawState) {
  clearSnapMarkerOnMap(this.map);
  try {
    (this.map as any).off(
      'draw.snap.options_changed',
      state.optionsChangedCallBAck
    );
  } catch {
    /* ignore */
  }
  return (MapboxDraw.modes.draw_polygon.onStop as Function).call(this, state);
};

export default CircleMode;
